import type { TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecordPdfT } from '../reports/record-pdf.js';
import { downloadFile, XLSX_TYPE } from '../reports/xlsx.js';
import type { ReportBrand } from '../voyage/source.js';
import { crewProfileFileName, crewProfilePdf } from './crew-profile-pdf.js';
import { movementsFileName, movementsWorkbook } from './movements-xlsx.js';
import { browserPdfOutput, pdfErrorKey, renderPdf } from './pdf.js';
import { employmentRecordFileName, employmentRecordPdf } from './record-of-employment-pdf.js';
import { errorCode, type PersonnelSource } from './source.js';
import type { CrewAssignment, CrewMovementRow, CrewProfileView, MovementTab } from './types.js';

export type PdfMode = 'save' | 'print';

/**
 * Personel dışa aktarımları (ADR 0131 Ek 3): Crew Profile PDF'i, Record of Employment ve hareket
 * listesinin Excel'i. PDF ve Excel istemcide, internetsiz üretilir; marka ya da belgeler
 * okunamazsa yine üretilir (marka boş, belgeler bölümünde not). Hata metni döner (`null`: başarılı).
 */
export function usePersonnelExport(source: PersonnelSource) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const tr: RecordPdfT = (key, options) => String(t(key, options ?? {}));
  const locale = i18n.resolvedLanguage ?? 'en';

  const pdf = async (
    name: string,
    mode: PdfMode,
    failed: string,
    build: (brand: ReportBrand | null, printedAt: string) => Promise<TDocumentDefinitions>,
  ): Promise<string | null> => {
    setBusy(true);
    try {
      const brand = (await source.brand?.().catch(() => null)) ?? null;
      const definition = await build(brand, new Date().toISOString());
      const output = source.pdf ?? browserPdfOutput();
      await output[mode](name, () => renderPdf(definition, { print: mode === 'print' }));
      return null;
    } catch (error) {
      const key = pdfErrorKey(errorCode(error));
      return key === 'restHours.pdf.error' ? failed : t(key);
    } finally {
      setBusy(false);
    }
  };

  const certificates = (userId: string) => source.crewCertificates(userId).catch(() => null);

  const profile = (view: CrewProfileView, mode: PdfMode) =>
    pdf(crewProfileFileName(view), mode, t('crewProfile.pdfFailed'), async (brand, printedAt) =>
      crewProfilePdf(tr, {
        view,
        scope: source.scope,
        certificates: await certificates(view.userId),
        brand,
        printedAt,
        locale,
      }),
    );

  const employment = (view: CrewProfileView, assignment: CrewAssignment, mode: PdfMode) =>
    pdf(
      employmentRecordFileName(view, assignment),
      mode,
      t('crewProfile.employment.failed'),
      async (brand, printedAt) =>
        employmentRecordPdf(tr, {
          view,
          assignment,
          certificates: await certificates(view.userId),
          brand,
          printedAt,
          locale,
        }),
    );

  const movements = async (
    rows: readonly CrewMovementRow[],
    tab: MovementTab,
    range: { from: string; to: string },
  ): Promise<string | null> => {
    setBusy(true);
    try {
      const bytes = await movementsWorkbook(rows, tab, tr, {
        ...range,
        generated: new Date().toISOString(),
      });
      const name = movementsFileName(tab, range);
      if (source.saveFile) await source.saveFile(name, bytes, XLSX_TYPE);
      else downloadFile(name, bytes, XLSX_TYPE);
      return null;
    } catch {
      return t('signOnOff.exportFailed');
    } finally {
      setBusy(false);
    }
  };

  return { busy, profile, employment, movements };
}
